import { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useOpenDetail } from "@/app/useOpenDetail";
import { useT } from "@/i18n";
import { ApiClientError } from "@/lib/api";
import { formatCurrencyCompact, formatQuarter } from "@/lib/format";
import { cn } from "@/lib/utils";
import { splitValues, type FilterSet } from "@/hooks/useFilterSet";
import { useStages, useYears } from "@/features/opportunities/hooks";
import { openStageIds } from "@/features/opportunities/pipelineFilters";
import { DetailedView } from "./DetailedView";
import { useAnalytics } from "./hooks";
import {
  ANALYTICS_DETAIL_PARAM,
  analyticsParams,
  detailTabFromSearch,
  pipelineHref,
  pipelineHrefWithout,
  type AnalyticsDetailTab,
} from "./params";
import { declarationApplies, tileFilterDeclarations, tileHrefFilters } from "./tileFilters";

/**
 * The summary strip: five compact tiles carrying the book's five operational metrics, and the
 * **Detailed view** that expands beneath them.
 *
 * Since 2026-08-25 this is the *whole* of analytics — the standalone screen is gone, and its
 * two surviving blocks moved into the tabs below (specs/analytics-strip-redesign.md). It
 * renders on both pipeline views from one component and one query, so the two cannot report
 * different numbers.
 *
 * Three rules are load-bearing rather than decorative:
 *
 * - **A zero is never shown while a figure is loading.** The tiles render as skeletons until
 *   the response lands, because the book is explicit that a false zero here is worse than a
 *   delay: a reader who sees "0 stalled" acts on it.
 * - **A tile highlights when the filters express exactly what its click applies** — from the
 *   *same* declaration the click is built from, so the two cannot drift. There is no
 *   "Filtrováno" line any more: the chips above the strip say once, with values, what five
 *   tiles used to repeat without them.
 * - **Presented on a wall, the numbers stay and the chrome goes.** The strip is the meeting's
 *   figures; the Detailed view and the toggle are things the room cannot act on.
 */
export function SummaryStrip({
  filters,
  year,
  /** Presented on a wall display: the same figures, at reading distance, without the chrome. */
  tv = false,
}: {
  filters: FilterSet;
  year: number | undefined;
  tv?: boolean;
}): React.JSX.Element {
  const t = useT();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const openDetail = useOpenDetail();
  const { apiParams } = filters;

  const params = useMemo(() => analyticsParams(apiParams, year), [apiParams, year]);
  const query = useAnalytics(params, { enabled: year !== undefined });
  // "Open" is a phase set, read from today's configuration — see `openStageIds`. Cached
  // reference data the filter panel has already fetched, so this costs no extra request.
  const stages = useStages();
  // The server's own year window, so "is this board the current year" is not this browser's
  // clock answering a question about the pipeline's calendar.
  const years = useYears();

  const detail = detailTabFromSearch(location.search);

  const setDetail = (next: AnalyticsDetailTab | null): void => {
    // Live read of the URL, per `useFilterParam`'s rule: a setter closed over a render-time
    // snapshot loses whatever another setter wrote in the same handler. The parameter is
    // deleted when the panel collapses, so a clean strip is a clean link.
    const search = new URLSearchParams(window.location.search);
    if (next === null) search.delete(ANALYTICS_DETAIL_PARAM);
    else search.set(ANALYTICS_DETAIL_PARAM, next);
    // `state` is carried explicitly: `setSearchParams` replaces the history entry, and a
    // replace without state erases the background a detail modal is drawn over.
    setSearchParams(search, { replace: true, state: location.state });
  };

  /**
   * A tile's link: the same view, new filters.
   *
   * It used to force the list (`view: "list"`), which threw a reader out of the board for
   * looking at a number — the tile is a filter, not a navigation. The view param is left
   * exactly as it was found.
   */
  const listHref = (extra: Record<string, string | undefined>): string =>
    pipelineHref(location.search, { filters: extra });

  /** The same view with one filter withdrawn — see `pipelineHrefWithout`. */
  const withoutHref = (keys: string[]): string => pipelineHrefWithout(location.search, keys);

  if (query.isPending) {
    return (
      <div className="mb-4">
        <StripFrame>
          {Array.from({ length: 5 }, (_, index) => (
            <TileSkeleton key={index} tv={tv} />
          ))}
        </StripFrame>
      </div>
    );
  }

  if (query.error || !query.data) {
    return (
      <div
        role="alert"
        className="border-destructive/40 bg-destructive/5 text-muted-foreground mb-4 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs"
      >
        <span>{t("strip.loadFailed")}</span>
        <code className="bg-muted rounded px-1 py-0.5 font-mono">
          {query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
        </code>
        <button type="button" onClick={() => void query.refetch()} className="text-primary font-medium hover:underline">
          {t("common.tryAgain")}
        </button>
      </div>
    );
  }

  const data = query.data;
  const { metrics } = data;
  const { quarter, year: periodYear } = metrics.closingThisQuarter;

  // One declaration per tile, shared by the click and the highlight. Every value in it is
  // something the filter panel shows and a reader can edit.
  const tiles = tileFilterDeclarations({ openStageIds: openStageIds(stages.data), quarter });
  // What the view is actually asserting, as sets — `apiParams` rather than `values`, because
  // `values` carries every def's default: `stalled` is a bool, so `values.stalled` reads
  // `["false"]` on an unfiltered board and the comparison below would see the key set on every
  // screen. `apiParams` is what the request carries, which is exactly the right yardstick for
  // "is this tile what the view expresses".
  const asserted = Object.fromEntries(
    Object.entries(filters.apiParams).map(([key, value]) => [key, splitValues(value)]),
  );
  // Set-equality across the whole tile vocabulary, order-insensitive: a tile lights when the
  // view is exactly what its click produces. Filters outside that vocabulary are ignored,
  // because the tile's number already respects them and its click leaves them alone.
  const active = (id: keyof typeof tiles): boolean => declarationApplies(asserted, tiles[id]);

  return (
    <div className="mb-4 space-y-2">
      <StripFrame>
        {/*
          * Three of these only *describe* (2026-08-26, asked for).
          *
          * Open opportunities, Pipeline value and Closing each report the whole of whatever is
          * on screen, so a click had no narrower set to reach — it re-asserted the view the
          * reader was already looking at. They keep their numbers and lose their links.
          */}
        <Tile label={t("strip.openCount")} value={String(metrics.openCount)} tv={tv} active={false} />
        <Tile
          label={t("strip.pipelineValue")}
          value={formatCurrencyCompact(metrics.pipelineValue)}
          tv={tv}
          active={false}
        />
        <Tile
          /*
           * "Closing this quarter" on the board anybody is normally looking at, and the period
           * by name on an archive year.
           *
           * The label carried its period so that a 2025 board's tile could not be misread as
           * today's (Decision Log row 24) — and "this quarter" on a 2025 board is exactly that
           * misreading. The current year comes from the server's own year window, not from
           * this browser's clock.
           */
          label={
            periodYear === years.data?.currentYear
              ? t("strip.closingThisQuarter")
              : t("strip.closing", formatQuarter(quarter, periodYear))
          }
          value={formatCurrencyCompact(metrics.closingThisQuarter.value)}
          tv={tv}
          active={false}
        />
        <Tile
          label={t("strip.stalled")}
          value={String(metrics.stalledCount)}
          tv={tv}
          /*
           * A toggle, not a one-way door (2026-08-26). Pressed while it is already the view,
           * it withdraws `stalled` and leaves every other filter alone — the reader who also
           * filtered to a manager did not ask for that to go. `aria-current` still carries the
           * pressed state, so the second press is announced as undoing the first.
           */
          to={active("stalled") ? withoutHref(["stalled"]) : listHref(tileHrefFilters(tiles.stalled))}
          active={active("stalled")}
        />
        <Tile
          label={t("strip.largest")}
          value={metrics.largest ? formatCurrencyCompact(metrics.largest.nip) : "—"}
          // The deal's own name, because "protect a significant deal" needs to know which.
          hint={metrics.largest?.name}
          tv={tv}
          // Straight to the record, not to a list of one: the largest tile's answer *is* an
          // opportunity, and the modal opens over the surface the reader is already on.
          onClick={metrics.largest ? () => openDetail(`/opportunity/${metrics.largest!.slug}`) : undefined}
          // Never highlighted: it opens a record rather than applying a filter, so there is no
          // filter state it could be the expression of.
          active={false}
        />
      </StripFrame>

      {/* Chrome, so it is absent on a wall display: nothing goes on the board that the room
          cannot act on. The strip's numbers stay. */}
      {!tv && (
        <div>
          <button
            type="button"
            onClick={() => setDetail(detail === null ? "phase" : null)}
            aria-expanded={detail !== null}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded text-xs font-medium focus-visible:ring-2 focus-visible:outline-none"
          >
            {t("analytics.detailedView")}
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", detail !== null && "rotate-180")} aria-hidden />
          </button>
        </div>
      )}

      {!tv && detail !== null && (
        <DetailedView data={data} tab={detail} onTabChange={setDetail} />
      )}
    </div>
  );
}

function StripFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="summary-strip">
      {children}
    </div>
  );
}

const TILE_CLASS =
  // The surface itself is `.metric-tile` in index.css — three gradient layers over a
  // translucent card, so the strip sits *in* the page's brand wash rather than over it.
  "metric-tile focus-visible:ring-ring block rounded-lg border px-3.5 py-2.5 text-left transition-shadow focus-visible:ring-2 focus-visible:outline-none";

/**
 * The accented state, and why it is a border *and* a background rather than one of them: the
 * strip is read at a glance and often on a projector, where a 1px hue change is nothing.
 */
const TILE_ACTIVE_CLASS = "metric-tile-active";

function Tile({
  label,
  value,
  hint,
  tv,
  to,
  onClick,
  active,
}: {
  label: string;
  value: string;
  hint?: string;
  tv: boolean;
  to?: string;
  onClick?: () => void;
  /** The current filters express exactly this tile's declared set. */
  active: boolean;
}) {
  const body = (
    <>
      <span className={cn("text-muted-foreground block", tv ? "text-sm" : "board-type-meta")}>{label}</span>
      <span className={cn("block font-semibold tabular-nums", tv ? "text-3xl" : "text-lg")}>{value}</span>
      {hint && (
        <span className={cn("text-muted-foreground block truncate", tv ? "text-xs" : "board-type-meta")}>{hint}</span>
      )}
    </>
  );

  // No dead-end numbers — but a tile whose figure has nothing behind it (no largest open
  // opportunity) is honestly inert rather than a link to an empty screen.
  if (to) {
    return (
      // `aria-current` rather than a colour alone: the highlight is a statement about the view,
      // and a statement has to reach a reader who cannot see the accent.
      <Link to={to} className={cn(TILE_CLASS, active && TILE_ACTIVE_CLASS)} aria-current={active ? "true" : undefined}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(TILE_CLASS, active && TILE_ACTIVE_CLASS)}>
        {body}
      </button>
    );
  }
  return <div className={cn(TILE_CLASS, "cursor-default")}>{body}</div>;
}

/** Structure, never a zero. See the class comment. */
function TileSkeleton({ tv }: { tv: boolean }) {
  const t = useT();

  return (
    <div className="metric-tile rounded-lg border px-3.5 py-2.5" aria-busy="true">
      <span className="sr-only">{t("strip.loading")}</span>
      <div className="bg-muted h-3 w-24 animate-pulse rounded" />
      <div className={cn("bg-muted mt-1.5 animate-pulse rounded", tv ? "h-8 w-32" : "h-5 w-20")} />
    </div>
  );
}
